import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { buildApiUrl } from "../utils/api";

const QUOTE_STATUSES = [
    'New lead',
    'Qualified',
    'Matching in progress',
    'Quote shared',
    'Negotiation',
    'Won',
    'On hold',
    'Lost'
];

const STATUS_COLORS = {
    'New lead': '#0d6efd',
    'Qualified': '#6f42c1',
    'Matching in progress': '#fd7e14',
    'Quote shared': '#0dcaf0',
    'Negotiation': '#ffc107',
    'Won': '#198754',
    'On hold': '#6c757d',
    'Lost': '#dc3545'
};

function UserWarehouseRequestList() {
    const [requests, setRequests] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [toast, setToast] = useState("");
    const [expandedRow, setExpandedRow] = useState(null);
    const itemsPerPage = 10;

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 3500);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(buildApiUrl("/api/warehouseQuote/getAllBookings"));
                const data = Array.isArray(res.data) ? res.data : [];
                setRequests(data);
                setFiltered(data);
            } catch (error) {
                console.error("Error fetching quote requests", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const q = search.toLowerCase().trim();
        let result = requests;

        if (q) {
            result = result.filter(r =>
                (r.userName || "").toLowerCase().includes(q) ||
                (r.userEmail || "").toLowerCase().includes(q) ||
                (r.warehouseName || "").toLowerCase().includes(q) ||
                (r.companyName || "").toLowerCase().includes(q)
            );
        }

        if (statusFilter) {
            result = result.filter(r => r.status === statusFilter);
        }

        setFiltered(result);
        setCurrentPage(1);
    }, [search, statusFilter, requests]);

    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentItems = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    const handleStatusChange = async (requestId, newStatus) => {
        try {
            await axios.put(buildApiUrl(`/api/warehouseQuote/updateBookingStatus/${requestId}`), { status: newStatus });
            setRequests(prev => prev.map(r => r._id === requestId ? { ...r, status: newStatus } : r));
            showToast(`Quote status updated to "${newStatus}"`);
        } catch (error) {
            console.error("Error updating status", error);
            showToast("Failed to update status. Please try again.");
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    return (
        <>
            <Helmet>
                <title>Quote Requests | CS Agent</title>
            </Helmet>

            {toast && <div className="admin-toast">{toast}</div>}

            <div className="userRequesterList">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h1 className="mb-0">
                        Quote Requests{" "}
                        <span className="badge bg-secondary fs-6">{filtered.length}</span>
                    </h1>
                    <div className="d-flex gap-2 flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            style={{ minWidth: "220px" }}
                            placeholder="Search name, email, warehouse..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <select
                            className="form-select"
                            style={{ minWidth: "160px" }}
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            <option value="">All statuses</option>
                            {QUOTE_STATUSES.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                        <thead className="custom-thead">
                            <tr>
                                <th>#</th>
                                <th>Date</th>
                                <th>Customer</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Company</th>
                                <th>Warehouse</th>
                                <th>Location</th>
                                <th>SKU</th>
                                <th>Orders/mo</th>
                                <th>Status</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map((r, index) => (
                                    <React.Fragment key={r._id}>
                                        <tr>
                                            <td>{indexOfFirst + index + 1}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{formatDate(r.bookingDate || r.createdAt)}</td>
                                            <td>{r.userName}</td>
                                            <td><a href={`mailto:${r.userEmail}`}>{r.userEmail}</a></td>
                                            <td>{r.userPhone}</td>
                                            <td>{r.companyName || "-"}</td>
                                            <td>{r.warehouseName}</td>
                                            <td>{r.warehouseLocation}</td>
                                            <td>{r.totalSKU}</td>
                                            <td>{r.averageMonthlyOrders}</td>
                                            <td>
                                                <select
                                                    className="form-select form-select-sm"
                                                    style={{ color: STATUS_COLORS[r.status] || "#000", minWidth: "155px" }}
                                                    value={r.status || "New lead"}
                                                    onChange={e => handleStatusChange(r._id, e.target.value)}
                                                >
                                                    {QUOTE_STATUSES.map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setExpandedRow(expandedRow === r._id ? null : r._id)}
                                                >
                                                    {expandedRow === r._id ? "Hide" : "View"}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedRow === r._id && (
                                            <tr className="table-light">
                                                <td colSpan="12">
                                                    <div className="p-3">
                                                        <div className="row g-3">
                                                            <div className="col-md-4">
                                                                <strong>Sales Channel:</strong> {r.salesChannel || "-"}<br />
                                                                <strong>Service Needs:</strong> {r.serviceNeeds || "-"}<br />
                                                                <strong>Regions:</strong> {r.fulfillmentRegions || "-"}<br />
                                                                <strong>Product Category:</strong> {r.productCategory || "-"}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>Storage Type:</strong> {r.storageType || "-"}<br />
                                                                <strong>Temperature:</strong> {r.temperatureRequirement || "-"}<br />
                                                                <strong>Go-Live:</strong> {r.preferredGoLiveDate || "-"}<br />
                                                                <strong>Contact Method:</strong> {r.preferredContactMethod || "-"}
                                                            </div>
                                                            <div className="col-md-4">
                                                                <strong>Warehouse Area:</strong> {r.warehouseArea || "-"}<br />
                                                                <strong>Company Website:</strong>{" "}
                                                                {r.companyWebsite
                                                                    ? <a href={r.companyWebsite} target="_blank" rel="noreferrer">{r.companyWebsite}</a>
                                                                    : "-"}<br />
                                                                <strong>Notes:</strong>
                                                                <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>{r.notes || "-"}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="12" className="text-center text-muted py-4">No quote requests found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <nav className="d-flex justify-content-center">
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>&laquo;</button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li className={`page-item ${currentPage === i + 1 ? "active" : ""}`} key={i}>
                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>&raquo;</button>
                            </li>
                        </ul>
                    </nav>
                )}
            </div>
        </>
    );
}

export default UserWarehouseRequestList;
